import { css, keyframes, useTheme } from '@emotion/react';
import { AudioLines, CheckCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { GenerationStatus } from '@/lib/api-client';
import { fontSizes, spacing } from '@/theme/tokens';

interface ProgressPanelProps {
  generation: GenerationStatus;
  onCancel: () => void;
  onRetry: (sectionId: string) => void;
  onDone: () => void;
}

const ACTIVE_STATES = new Set(['pending', 'tone_analysis', 'generating_music']);

function getPhaseLabel(status: GenerationStatus['status']): string {
  switch (status) {
    case 'pending':
      return 'Preparing...';
    case 'tone_analysis':
      return 'Analyzing tone...';
    case 'generating_music':
      return 'Generating music...';
    case 'completed':
      return 'Music ready!';
    case 'failed':
      return 'Generation failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return '';
  }
}

function formatTimeRemaining(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `~${minutes}:${seconds.toString().padStart(2, '0')} remaining`;
}

const bounce = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`;

function SoundWave({ color }: { color: string }) {
  const delays = [0, 0.2, 0.4, 0.15, 0.35, 0.1, 0.3, 0.25, 0.45, 0.05];
  const heights = [0.5, 0.7, 1, 0.6, 0.9, 0.4, 0.8, 0.65, 0.75, 0.45];

  return (
    <div
      aria-hidden="true"
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        height: 48px;
        margin: 0 auto ${spacing.md};
      `}
    >
      {delays.map((delay, i) => (
        <div
          key={i}
          css={css`
            width: 4px;
            height: ${heights[i] * 48}px;
            background: ${color};
            border-radius: 2px;
            animation: ${bounce} ${0.8 + heights[i] * 0.4}s ease-in-out ${delay}s infinite;
            transform-origin: center;
          `}
        />
      ))}
    </div>
  );
}

export function ProgressPanel({ generation, onCancel, onDone }: ProgressPanelProps) {
  const theme = useTheme();
  const isActive = ACTIVE_STATES.has(generation.status);
  const isCompleted = generation.status === 'completed';
  const isTerminalError = generation.status === 'failed' || generation.status === 'cancelled';

  return (
    <div
      css={css`
        border: 1px solid ${theme.colors.border};
        border-radius: 12px;
        background: ${theme.colors.surface};
        padding: ${spacing.lg};
        text-align: center;
        margin-top: ${spacing.md};
      `}
    >
      {isActive && <SoundWave color={theme.colors.primary} />}

      {isCompleted && (
        <CheckCircle
          size={40}
          aria-hidden="true"
          css={css`
            color: ${theme.colors.success};
            margin-bottom: ${spacing.md};
          `}
        />
      )}

      {isTerminalError && (
        <AudioLines
          size={40}
          aria-hidden="true"
          css={css`
            color: ${theme.colors.error};
            margin-bottom: ${spacing.md};
          `}
        />
      )}

      <div
        css={css`
          font-size: ${fontSizes.md};
          font-weight: 600;
          color: ${theme.colors.text};
          margin-bottom: ${spacing.md};
        `}
      >
        {getPhaseLabel(generation.status)}
      </div>

      {isActive && (
        <>
          <div
            role="progressbar"
            aria-valuenow={generation.progress}
            aria-valuemin={0}
            aria-valuemax={100}
            css={css`
              height: 4px;
              background: ${theme.colors.border};
              border-radius: 2px;
              overflow: hidden;
              margin-bottom: ${spacing.sm};
            `}
          >
            <div
              css={css`
                height: 100%;
                width: ${generation.progress}%;
                background: ${theme.colors.primary};
                border-radius: 2px;
                transition: width 0.3s ease;
              `}
            />
          </div>

          <div
            css={css`
              font-size: ${fontSizes.xs};
              color: ${theme.colors.textSecondary};
              margin-bottom: ${spacing.md};
            `}
          >
            {generation.estimatedTimeRemainingMs != null
              ? formatTimeRemaining(generation.estimatedTimeRemainingMs)
              : `${Math.round(generation.progress)}%`}
          </div>

          <button
            type="button"
            onClick={onCancel}
            css={css`
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
              background: none;
              border: none;
              cursor: pointer;
              padding: 0;

              &:hover {
                color: ${theme.colors.text};
                text-decoration: underline;
              }
            `}
          >
            Cancel
          </button>
        </>
      )}

      {isCompleted && (
        <Button
          size="sm"
          onClick={onDone}
          css={css`
            display: inline-flex;
            align-items: center;
            gap: ${spacing.sm};
          `}
        >
          Done
        </Button>
      )}

      {isTerminalError && (
        <button
          type="button"
          onClick={onDone}
          css={css`
            font-size: ${fontSizes.sm};
            color: ${theme.colors.textSecondary};
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;

            &:hover {
              color: ${theme.colors.text};
              text-decoration: underline;
            }
          `}
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
